import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, FormControl, Grid,
  InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tab, Tabs, TextField, Typography,
  useTheme
} from "@mui/material";
import AssessmentIcon from '@mui/icons-material/Assessment';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import { format, subDays } from "date-fns";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

// Estilos como objeto de `sx`. Não usar makeStyles do @mui/styles: o app monta o
// ThemeProvider do @mui/material v5 e o @mui/styles instalado é v6 — contextos
// diferentes, o tema não chega e quebra em "theme.spacing is not a function"
// (foi o que derrubou esta tela na Rio Brás em 17/08).
const classes = {
  container: { pt: 4, pb: 4 },
  paper: { p: 2, mb: 2 },
  filterBar: { p: 2, mb: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" },
  dateField: { minWidth: 160 },
  selectField: { minWidth: 180 },
  btnGroup: { display: "flex", gap: 1, ml: "auto", alignItems: "flex-end" },
  summaryCard: { p: 2, textAlign: "center" },
  summaryValue: { fontSize: "1.8rem", fontWeight: 700, color: "primary.main" },
  summaryLabel: { fontSize: "0.8rem", color: "text.secondary", mt: 0.5 },
  tableHeader: { bgcolor: "primary.main", "& th": { color: "#fff", fontWeight: 700, fontSize: "0.85rem" } },
  chartTitle: { fontWeight: 600, mb: 1 },
  loading: { display: "flex", justifyContent: "center", p: 4 },
  noData: { textAlign: "center", p: 4, color: "text.secondary" },
  tabs: { mb: 2, borderBottom: 1, borderColor: "divider" }
};

const COLORS = ["#2E75B6","#E67E22","#27AE60","#8E44AD","#E74C3C","#16A085","#F39C12","#3498DB","#D35400","#1ABC9C","#9B59B6","#2C3E50"];
const CLASS_COLORS = { A: "#27AE60", B: "#F39C12", C: "#E74C3C" };

const Reports = () => {
  const theme = useTheme();
  const today = format(new Date(), "yyyy-MM-dd");
  const thirtyDaysAgo = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
  const [selectedQueues, setSelectedQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [queues, setQueues] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [uRes, wRes, qRes] = await Promise.all([
          api.get("/users"), api.get("/whatsapp"), api.get("/queue")
        ]);
        setUsers(uRes.data.users || uRes.data || []);
        setWhatsapps(wRes.data || []);
        setQueues(qRes.data || []);
      } catch (err) { console.error("Error loading filter options", err); }
    };
    loadOptions();
  }, []);

  const buildParams = useCallback(() => {
    const params = { startDate, endDate };
    if (selectedUsers.length) params.userIds = JSON.stringify(selectedUsers);
    if (selectedWhatsapps.length) params.whatsappIds = JSON.stringify(selectedWhatsapps);
    if (selectedQueues.length) params.queueIds = JSON.stringify(selectedQueues);
    return params;
  }, [startDate, endDate, selectedUsers, selectedWhatsapps, selectedQueues]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get("/reports", { params: buildParams() });
      setData(result);
    } catch (err) { toast.error("Erro ao gerar relatório"); }
    finally { setLoading(false); }
  }, [buildParams]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get("/reports/export", { params: buildParams(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Relatorio_${startDate}_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Erro ao exportar"); }
    finally { setExporting(false); }
  };

  return (
    <Container maxWidth="lg" sx={classes.container}>
      <Typography variant="h5" gutterBottom>
        <AssessmentIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
        Relatórios
      </Typography>

      <Paper sx={classes.filterBar} elevation={1}>
        <TextField label="Início" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={classes.dateField} />
        <TextField label="Fim" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={classes.dateField} />
        <FormControl sx={classes.selectField}>
          <InputLabel>Atendente</InputLabel>
          <Select multiple value={selectedUsers} onChange={e => setSelectedUsers(e.target.value)}
            renderValue={sel => sel.map(id => { const u = users.find(u => u.id === id); return u ? u.name : id; }).join(", ")}>
            {users.map(u => (<MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl sx={classes.selectField}>
          <InputLabel>Conexão</InputLabel>
          <Select multiple value={selectedWhatsapps} onChange={e => setSelectedWhatsapps(e.target.value)}
            renderValue={sel => sel.map(id => { const w = whatsapps.find(w => w.id === id); return w ? w.name : id; }).join(", ")}>
            {whatsapps.map(w => (<MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>))}
          </Select>
        </FormControl>
        <FormControl sx={classes.selectField}>
          <InputLabel>Setor</InputLabel>
          <Select multiple value={selectedQueues} onChange={e => setSelectedQueues(e.target.value)}
            renderValue={sel => sel.map(id => { const q = queues.find(q => q.id === id); return q ? q.name : id; }).join(", ")}>
            {queues.map(q => (<MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>))}
          </Select>
        </FormControl>
        <Box sx={classes.btnGroup}>
          <Button variant="contained" color="primary" onClick={fetchReport} disabled={loading}>
            {loading ? <CircularProgress size={22} /> : "Gerar"}
          </Button>
          {data && (
            <Button variant="outlined" color="primary" onClick={handleExport} disabled={exporting}
              startIcon={exporting ? <CircularProgress size={18} /> : <GetAppIcon />}>
              Excel
            </Button>
          )}
        </Box>
      </Paper>

      {loading && <Box sx={classes.loading}><CircularProgress /></Box>}
      {!loading && !data && <Paper sx={classes.noData}><Typography>Selecione um período e clique em Gerar</Typography></Paper>}

      {!loading && data && (
        <>
          <Paper>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
              <Tab label="Por Conexão" />
              <Tab label="Ativo × Receptivo" />
              <Tab label="Por Atendente" />
              <Tab label="Por Ticket" />
              <Tab label="Curva ABC Clientes" />
              <Tab label="Uptime Conexões" />
            </Tabs>
          </Paper>

          {tab === 0 && <TabByConnection data={data} classes={classes} theme={theme} />}
          {tab === 1 && <TabActiveReceptive data={data} classes={classes} />}
          {tab === 2 && <TabByUser data={data} classes={classes} theme={theme} />}
          {tab === 3 && <TabByTicket data={data} classes={classes} />}
          {tab === 4 && <TabABC data={data} classes={classes} />}
          {tab === 5 && <TabUptime data={data} classes={classes} />}
        </>
      )}
    </Container>
  );
};

const SummaryCards = ({ items }) => (
  <Grid container spacing={2} style={{ marginBottom: 16 }}>
    {items.map((c, i) => (
      <Grid item xs={6} sm={4} md key={i}>
        <Paper sx={classes.summaryCard} elevation={1}>
          <Box sx={classes.summaryValue}>{c.value}</Box>
          <Box sx={classes.summaryLabel}>{c.label}</Box>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

const TabByConnection = ({ data, classes, theme }) => {
  const totals = data.connections.reduce((a, r) => ({
    abertos: a.abertos + Number(r.tickets_abertos),
    fechados: a.fechados + Number(r.tickets_fechados),
    clientes: a.clientes + Number(r.clientes_distintos),
    enviadas: a.enviadas + Number(r.mensagens_enviadas),
    recebidas: a.recebidas + Number(r.mensagens_recebidas),
    ativos: a.ativos + Number(r.tickets_ativos),
    receptivos: a.receptivos + Number(r.tickets_receptivos)
  }), { abertos: 0, fechados: 0, clientes: 0, enviadas: 0, recebidas: 0, ativos: 0, receptivos: 0 });

  const pieData = data.connections.map(r => ({ name: r.conexao, value: Number(r.tickets_abertos) }));
  const barData = data.connections.map(r => ({
    name: r.conexao,
    "Clientes": Number(r.clientes_distintos),
    "Msg Env": Number(r.mensagens_enviadas),
    "Msg Rec": Number(r.mensagens_recebidas)
  }));

  return (
    <>
      <SummaryCards items={[
        { label: "Clientes Distintos", value: totals.clientes },
        { label: "Msgs Enviadas", value: totals.enviadas },
        { label: "Msgs Recebidas", value: totals.recebidas },
        { label: "Ativos / Receptivos", value: `${totals.ativos} / ${totals.receptivos}` }
      ]} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={classes.paper}>
            <Typography sx={classes.chartTitle}>Volume por Conexão</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Clientes" fill="#2E75B6" />
                <Bar dataKey="Msg Env" fill="#27AE60" />
                <Bar dataKey="Msg Rec" fill="#8E44AD" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={classes.paper}>
            <Typography sx={classes.chartTitle}>Distribuição de Tickets</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                  {pieData.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={classes.paper}>
        <Typography sx={classes.chartTitle}>Resumo por Conexão</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead sx={classes.tableHeader}>
              <TableRow>
                <TableCell>Conexão</TableCell>
                <TableCell align="center">Clientes</TableCell>
                <TableCell align="center">Ativos</TableCell>
                <TableCell align="center">Receptivos</TableCell>
                <TableCell align="center">Msg Env.</TableCell>
                <TableCell align="center">Msg Rec.</TableCell>
                <TableCell align="center">1ª Resp. média (min)</TableCell>
                <TableCell align="center">1ª Resp. total (min)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.connections.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell>{r.conexao}</TableCell>
                  <TableCell align="center">{r.clientes_distintos}</TableCell>
                  <TableCell align="center">{r.tickets_ativos}</TableCell>
                  <TableCell align="center">{r.tickets_receptivos}</TableCell>
                  <TableCell align="center">{r.mensagens_enviadas}</TableCell>
                  <TableCell align="center">{r.mensagens_recebidas}</TableCell>
                  <TableCell align="center">{r.tempo_medio_primeira_resposta_min != null ? r.tempo_medio_primeira_resposta_min : "-"}</TableCell>
                  <TableCell align="center"><strong>{r.tempo_total_primeira_resposta_min != null ? r.tempo_total_primeira_resposta_min : "-"}</strong></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={classes.paper}>
        <Typography sx={classes.chartTitle}>Diário por Conexão</Typography>
        <TableContainer style={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead sx={classes.tableHeader}>
              <TableRow>
                <TableCell>Conexão</TableCell>
                <TableCell align="center">Data</TableCell>
                <TableCell align="center">Clientes</TableCell>
                <TableCell align="center">Ativos</TableCell>
                <TableCell align="center">Receptivos</TableCell>
                <TableCell align="center">Msg Env.</TableCell>
                <TableCell align="center">Msg Rec.</TableCell>
                <TableCell align="center">1ª Resp. média (min)</TableCell>
                <TableCell align="center">1ª Resp. total (min)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.dailyByConnection.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell>{r.conexao}</TableCell>
                  <TableCell align="center">{format(new Date(r.dia + "T12:00:00"), "dd/MM/yyyy")}</TableCell>
                  <TableCell align="center">{r.clientes_distintos}</TableCell>
                  <TableCell align="center">{r.tickets_ativos}</TableCell>
                  <TableCell align="center">{r.tickets_receptivos}</TableCell>
                  <TableCell align="center">{r.mensagens_enviadas}</TableCell>
                  <TableCell align="center">{r.mensagens_recebidas}</TableCell>
                  <TableCell align="center">{r.tempo_medio_primeira_resposta_min != null ? r.tempo_medio_primeira_resposta_min : "-"}</TableCell>
                  <TableCell align="center"><strong>{r.tempo_total_primeira_resposta_min != null ? r.tempo_total_primeira_resposta_min : "-"}</strong></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

const TabActiveReceptive = ({ data, classes }) => {
  const chartData = data.dailyByConnection.reduce((acc, r) => {
    const key = r.dia;
    if (!acc[key]) acc[key] = { dia: key, ativos: 0, receptivos: 0 };
    acc[key].ativos += Number(r.tickets_ativos);
    acc[key].receptivos += Number(r.tickets_receptivos);
    return acc;
  }, {});
  const series = Object.values(chartData).sort((a, b) => a.dia.localeCompare(b.dia));

  const totalAtivos = data.connections.reduce((s, r) => s + Number(r.tickets_ativos), 0);
  const totalReceptivos = data.connections.reduce((s, r) => s + Number(r.tickets_receptivos), 0);
  const total = totalAtivos + totalReceptivos;
  const pctAtivos = total > 0 ? Math.round((totalAtivos / total) * 100) : 0;

  return (
    <>
      <SummaryCards items={[
        { label: "Ativos (vendedor iniciou)", value: totalAtivos },
        { label: "Receptivos (cliente iniciou)", value: totalReceptivos },
        { label: "% Proatividade", value: `${pctAtivos}%` },
        { label: "Total", value: total }
      ]} />

      <Paper sx={classes.paper}>
        <Typography sx={classes.chartTitle}>Ativos × Receptivos por Dia (todas conexões)</Typography>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={series}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="dia" tick={{ fontSize: 11 }} tickFormatter={d => format(new Date(d + "T12:00:00"), "dd/MM")} />
            <YAxis />
            <Tooltip labelFormatter={d => format(new Date(d + "T12:00:00"), "dd/MM/yyyy")} />
            <Legend />
            <Bar dataKey="ativos" name="Ativos" fill="#27AE60" stackId="a" />
            <Bar dataKey="receptivos" name="Receptivos" fill="#E67E22" stackId="a" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Paper sx={classes.paper}>
        <Typography sx={classes.chartTitle}>Proatividade por Conexão</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead sx={classes.tableHeader}>
              <TableRow>
                <TableCell>Conexão</TableCell>
                <TableCell align="center">Ativos</TableCell>
                <TableCell align="center">Receptivos</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="center">% Proativo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.connections.map((r, i) => {
                const t = Number(r.tickets_ativos) + Number(r.tickets_receptivos);
                const pct = t > 0 ? Math.round((Number(r.tickets_ativos) / t) * 100) : 0;
                return (
                  <TableRow key={i} hover>
                    <TableCell>{r.conexao}</TableCell>
                    <TableCell align="center">{r.tickets_ativos}</TableCell>
                    <TableCell align="center">{r.tickets_receptivos}</TableCell>
                    <TableCell align="center">{t}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={`${pct}%`} style={{ backgroundColor: pct >= 50 ? "#27AE60" : pct >= 25 ? "#F39C12" : "#E74C3C", color: "#fff", fontWeight: 600 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

const TabByUser = ({ data, classes, theme }) => {
  const totals = data.summary.reduce((acc, r) => ({
    clientes: acc.clientes + Number(r.clientes_distintos),
    abertos: acc.abertos + Number(r.tickets_abertos),
    fechados: acc.fechados + Number(r.tickets_fechados),
    msgs: acc.msgs + Number(r.mensagens_enviadas)
  }), { clientes: 0, abertos: 0, fechados: 0, msgs: 0 });

  const barData = data.summary.map(r => ({
    name: r.atendente,
    "Clientes": Number(r.clientes_distintos),
    "Abertos": Number(r.tickets_abertos),
    "Fechados": Number(r.tickets_fechados)
  }));

  return (
    <>
      <SummaryCards items={[
        { label: "Clientes Distintos", value: totals.clientes },
        { label: "Abertos", value: totals.abertos },
        { label: "Fechados", value: totals.fechados },
        { label: "Msgs Enviadas", value: totals.msgs },
        { label: "Taxa Resolução", value: totals.abertos > 0 ? `${Math.round((totals.fechados / totals.abertos) * 100)}%` : "0%" }
      ]} />

      {data.responseBasis && (
        <Typography variant="caption" component="p" style={{ marginBottom: 12, opacity: 0.75, lineHeight: 1.5 }}>
          <strong>Como a 1ª resposta é contada:</strong> vale a primeira mensagem de uma pessoa da equipe —
          saudação, menu, ausência, intervalo, despedida e avisos do sistema não entram.{" "}
          {data.responseBasis.expedienteAplicado
            ? `O tempo é medido dentro do expediente (${data.responseBasis.setoresComExpediente} setor(es) com horário configurado${data.responseBasis.feriadosConsiderados > 0 ? `, ${data.responseBasis.feriadosConsiderados} feriado(s) cadastrado(s)` : ""}); a coluna "Corrida" mostra o tempo de relógio.`
            : "Nenhum setor tem horário configurado, então o tempo é de relógio (corrido)."}
        </Typography>
      )}

      <Paper sx={classes.paper}>
        <Typography sx={classes.chartTitle}>Produtividade por Atendente</Typography>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={barData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Clientes" fill="#2E75B6" />
            <Bar dataKey="Abertos" fill="#E67E22" />
            <Bar dataKey="Fechados" fill="#27AE60" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Paper sx={classes.paper}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={classes.tableHeader}>
              <TableRow>
                <TableCell>Atendente</TableCell>
                <TableCell align="center">Clientes</TableCell>
                <TableCell align="center">Abertos</TableCell>
                <TableCell align="center">Fechados</TableCell>
                <TableCell align="center">Mensagens</TableCell>
                <TableCell align="center">1ª Resp. média (min)</TableCell>
                <TableCell align="center">1ª Resp. total (min)</TableCell>
                <TableCell align="center">Corrida (min)</TableCell>
                <TableCell align="center">Resolução (min)</TableCell>
                <TableCell align="center">Taxa</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.summary.map((r, i) => {
                const taxa = r.tickets_abertos > 0 ? Math.round((r.tickets_fechados / r.tickets_abertos) * 100) : 0;
                return (
                  <TableRow key={i} hover>
                    <TableCell>{r.atendente}</TableCell>
                    <TableCell align="center">{r.clientes_distintos}</TableCell>
                    <TableCell align="center">{r.tickets_abertos}</TableCell>
                    <TableCell align="center">{r.tickets_fechados}</TableCell>
                    <TableCell align="center">{r.mensagens_enviadas}</TableCell>
                    <TableCell align="center">{r.tempo_medio_primeira_resposta_min ?? "-"}</TableCell>
                    <TableCell align="center"><strong>{r.tempo_total_primeira_resposta_min ?? "-"}</strong></TableCell>
                    <TableCell align="center" style={{ opacity: 0.65 }}>{r.tempo_medio_primeira_resposta_corrido_min ?? "-"}</TableCell>
                    <TableCell align="center">{r.tempo_medio_resolucao_min ?? "-"}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={`${taxa}%`} style={{ backgroundColor: taxa >= 80 ? "#27AE60" : taxa >= 50 ? "#F39C12" : "#E74C3C", color: "#fff", fontWeight: 600 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

// Detalhamento por ticket — a linha individual por trás das médias da aba
// "Por Atendente". Diferente das outras abas, o período aqui é "teve conversa
// no período" e o cronômetro começa na 1ª mensagem do cliente, não na criação
// do ticket (ver comentário em ReportService.ts): é o que faz o ticket
// REABERTO aparecer com um tempo de espera que significa alguma coisa.
const hhmmss = (min) => {
  if (min === null || min === undefined || !Number.isFinite(Number(min))) return "-";
  const total = Math.round(Number(min) * 60);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const dois = (n) => String(n).padStart(2, "0");
  return `${h}:${dois(m)}:${dois(s)}`;
};

const soHora = (dt) => (dt ? String(dt).slice(11, 16) : "-");
const dataHora = (dt) => (dt ? `${String(dt).slice(8, 10)}/${String(dt).slice(5, 7)} ${String(dt).slice(11, 16)}` : "-");

const TabByTicket = ({ data, classes }) => {
  const linhas = data.tickets || [];
  const respondidos = linhas.filter(r => r.primeira_resposta);
  const semResposta = linhas.length - respondidos.length;
  const somaEspera = respondidos.reduce((a, r) => a + Number(r.espera_min || 0), 0);
  const media = respondidos.length > 0 ? somaEspera / respondidos.length : null;
  const pior = respondidos.reduce((a, r) => (Number(r.espera_min) > Number(a?.espera_min ?? -1) ? r : a), null);

  return (
    <>
      <SummaryCards items={[
        { label: "Atendimentos no período", value: linhas.length },
        { label: "Com resposta", value: respondidos.length },
        { label: "Sem resposta", value: semResposta },
        { label: "Espera média", value: hhmmss(media) },
        { label: "Maior espera", value: hhmmss(pior?.espera_min) }
      ]} />

      <Typography variant="caption" component="p" style={{ marginBottom: 12, opacity: 0.75, lineHeight: 1.5 }}>
        <strong>Uma linha por atendimento.</strong> Entram os tickets em que o cliente falou dentro do período —
        inclusive os que já existiam e voltaram a conversar. A espera é contada da{" "}
        <strong>1ª mensagem do cliente</strong> até a <strong>1ª resposta de uma pessoa</strong> da equipe
        (saudação, menu e avisos automáticos não contam){data.responseBasis?.expedienteAplicado
          ? ", medida dentro do expediente do setor" : ""}. Conversas de <strong>grupo</strong> não entram nesta aba.
      </Typography>

      {data.ticketsTruncados && (
        <Typography variant="body2" style={{ marginBottom: 12, color: "#C00000", fontWeight: 600 }}>
          O período devolveu mais linhas do que a tela consegue mostrar e a lista foi cortada. Reduza o intervalo de datas.
        </Typography>
      )}

      <Paper sx={classes.paper}>
        <Typography sx={classes.chartTitle}>Atendimentos por Ticket</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead sx={classes.tableHeader}>
              <TableRow>
                <TableCell align="center">Ticket</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Número</TableCell>
                <TableCell>Setor</TableCell>
                <TableCell>Atendente</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Criado</TableCell>
                <TableCell align="center">1ª msg do cliente</TableCell>
                <TableCell align="center">1ª resposta</TableCell>
                <TableCell align="center">Espera</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linhas.map((r, i) => (
                <TableRow key={i} hover style={r.primeira_resposta ? undefined : { backgroundColor: "rgba(231,76,60,0.10)" }}>
                  <TableCell align="center">{r.ticketId}</TableCell>
                  <TableCell>{r.cliente}</TableCell>
                  <TableCell>{r.numero}</TableCell>
                  <TableCell>{r.setor}</TableCell>
                  <TableCell>{r.atendente}</TableCell>
                  <TableCell align="center">{r.status}</TableCell>
                  <TableCell align="center" style={{ opacity: 0.65 }}>{dataHora(r.criacao)}</TableCell>
                  <TableCell align="center">{dataHora(r.primeira_mensagem_cliente)}</TableCell>
                  <TableCell align="center">
                    {/* Só a hora quando é no mesmo dia da mensagem; se a resposta
                        caiu em outro dia, mostra a data também — senão parece que
                        a equipe respondeu ANTES do cliente falar. */}
                    {r.primeira_resposta
                      ? (String(r.primeira_resposta).slice(0, 10) === String(r.primeira_mensagem_cliente).slice(0, 10)
                          ? soHora(r.primeira_resposta)
                          : dataHora(r.primeira_resposta))
                      : "—"}
                  </TableCell>
                  <TableCell align="center">
                    {r.primeira_resposta
                      ? <strong>{hhmmss(r.espera_min)}</strong>
                      : <Chip size="small" label="sem resposta" style={{ backgroundColor: "#E74C3C", color: "#fff", fontWeight: 600 }} />}
                  </TableCell>
                </TableRow>
              ))}
              {linhas.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center">Nenhum atendimento com mensagem do cliente neste período.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

const TabABC = ({ data, classes }) => {
  const summary = data.abc.reduce((acc, r) => {
    acc[r.classe].count += 1;
    acc[r.classe].msgs += Number(r.total_mensagens);
    return acc;
  }, { A: { count: 0, msgs: 0 }, B: { count: 0, msgs: 0 }, C: { count: 0, msgs: 0 } });

  const chartData = data.abc.map((r, i) => ({
    idx: i + 1,
    cliente: r.cliente.substring(0, 20),
    mensagens: Number(r.total_mensagens),
    acumulado: Number(r.percentual_acumulado),
    classe: r.classe
  }));

  return (
    <>
      <SummaryCards items={[
        { label: "Clientes Classe A (80% volume)", value: summary.A.count },
        { label: "Clientes Classe B (15%)", value: summary.B.count },
        { label: "Clientes Classe C (5%)", value: summary.C.count },
        { label: "Total Clientes", value: data.abc.length }
      ]} />

      <Paper sx={classes.paper}>
        <Typography sx={classes.chartTitle}>Curva ABC — % Acumulado</Typography>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="idx" tick={{ fontSize: 10 }} label={{ value: "Ranking cliente", position: "insideBottom", offset: -5 }} />
            <YAxis yAxisId="l" label={{ value: "Msgs", angle: -90, position: "insideLeft" }} />
            <YAxis yAxisId="r" orientation="right" domain={[0, 100]} label={{ value: "% acum", angle: 90, position: "insideRight" }} />
            <Tooltip />
            <Legend />
            <Bar yAxisId="l" dataKey="mensagens" name="Mensagens">
              {chartData.map((d, i) => <Cell key={i} fill={CLASS_COLORS[d.classe]} />)}
            </Bar>
            <Line yAxisId="r" type="monotone" dataKey="acumulado" name="% Acumulado" stroke="#2E75B6" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      <Paper sx={classes.paper}>
        <TableContainer style={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead sx={classes.tableHeader}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell align="center">Tickets</TableCell>
                <TableCell align="center">Mensagens</TableCell>
                <TableCell align="center">% do Total</TableCell>
                <TableCell align="center">% Acumulado</TableCell>
                <TableCell align="center">Classe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.abc.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{r.cliente}</TableCell>
                  <TableCell align="center">{r.total_tickets}</TableCell>
                  <TableCell align="center">{r.total_mensagens}</TableCell>
                  <TableCell align="center">{r.percentual}%</TableCell>
                  <TableCell align="center">{r.percentual_acumulado}%</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={r.classe} style={{ backgroundColor: CLASS_COLORS[r.classe], color: "#fff", fontWeight: 700 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

const TabUptime = ({ data, classes }) => {
  const hasData = data.uptime && data.uptime.length > 0;
  return (
    <>
      {!hasData && (
        <Paper sx={classes.paper}>
          <Typography color="textSecondary">
            Sem registros de desconexão no período. O log de conexão passa a ser gravado a partir da instalação deste módulo.
          </Typography>
        </Paper>
      )}

      {hasData && (
        <>
          <Paper sx={classes.paper}>
            <Typography sx={classes.chartTitle}>Uptime por Conexão</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={classes.tableHeader}>
                  <TableRow>
                    <TableCell>Conexão</TableCell>
                    <TableCell align="center">Total (min)</TableCell>
                    <TableCell align="center">Offline (min)</TableCell>
                    <TableCell align="center">Online (min)</TableCell>
                    <TableCell align="center">Uptime %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.uptime.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{r.conexao}</TableCell>
                      <TableCell align="center">{r.total_minutos_periodo}</TableCell>
                      <TableCell align="center">{r.minutos_offline}</TableCell>
                      <TableCell align="center">{r.minutos_online}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={`${r.uptime_percentual}%`} style={{ backgroundColor: r.uptime_percentual >= 99 ? "#27AE60" : r.uptime_percentual >= 95 ? "#F39C12" : "#E74C3C", color: "#fff", fontWeight: 600 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={classes.paper}>
            <Typography sx={classes.chartTitle}>Incidentes de Desconexão</Typography>
            <TableContainer style={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead sx={classes.tableHeader}>
                  <TableRow>
                    <TableCell>Conexão</TableCell>
                    <TableCell>Início</TableCell>
                    <TableCell>Fim</TableCell>
                    <TableCell align="center">Duração (min)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.uptime.flatMap((u, ui) => u.incidentes.map((inc, ii) => (
                    <TableRow key={`${ui}-${ii}`} hover>
                      <TableCell>{u.conexao}</TableCell>
                      <TableCell>{new Date(inc.inicio).toLocaleString("pt-BR")}</TableCell>
                      <TableCell>{inc.fim ? new Date(inc.fim).toLocaleString("pt-BR") : <em>em aberto</em>}</TableCell>
                      <TableCell align="center">{inc.duracao_min ?? "-"}</TableCell>
                    </TableRow>
                  )))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </>
  );
};

export default Reports;
